import {
    Currency, CurrencySettings, DateUtil, Model, ModelRow, ModelSearchResult, Navigation, ObjectUtil, UrlUtil
} from "@mcleod/core";
import {
    Button, ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceRemoteDataChangeEvent, Label, Layout,
    ModelDataChangeType, Panel, Snackbar, Table, TableRowDisplayEvent, TableSelectionEvent
} from "@mcleod/components";
import { FilterPanel } from "@mcleod/dispatch";
import { AutogenLayoutDriverSettlement } from "./autogen/AutogenLayoutDriverSettlement";
import { DriverSettlementFilter } from "./DriverSettlementFilter";
import { PayablesLookup } from "../OrderPayables";
import { getPayrollControlValue, LMEUserSettings } from "@mcleod/general";
import { OrderPayablesUtil } from "../OrderPayablesUtil";
import { ArchiveVoidedPayables } from "../ArchiveVoidedPayables";
import { DriverSettlementProcess } from "./DriverSettlementProcess";
import { PayeeDataLookup, SettlementPayeeData, SettlementPayeeDataLookup } from "../PayeeData";
import { PayeeSelectedProps } from "./DriverSettlementGroupByTable";
import {
    ModelDriverSettlementProfile, RowDriverSettlementProfile
} from "../../driver-settlement/src/models/ModelDriverSettlementProfile";
import { TimecardProcessing } from "../TimecardProcessing";
import { RowPayrollControlFields } from "@mcleod/general/src/models/autogen/AutogenModelPayrollControl";

export interface DriverSettlementTabLayout extends Layout {
    tableDriverSettlement: Table;
    getLayoutBaseFilter: (row: ModelRow) => boolean;
    filterPanel: FilterPanel;
    getProfileRow: () => RowDriverSettlementProfile;

}

export class DriverSettlement extends AutogenLayoutDriverSettlement {
    private _profileRow: RowDriverSettlementProfile;
    private profileFilter: DriverSettlementFilter;
    private payablesLookupMap: PayablesLookup;
    private payeeDataLookup: SettlementPayeeDataLookup;
    private timerId: number | null = null;
    private currentSelectedPayee: PayeeSelectedProps | null = null;
    private countdownTimerId: number | null = null;
    private remainingSeconds: number = 30;
    public snackbars: Set<Panel> = new Set();
    private checkingBonusTaxRates: boolean = false;
    private payrollCutoffDate: Date | null = null;
    private recurringDate: Date | null = null;
    public payrollControl: ModelRow;
    public updateCutoffDate: (profile: ModelRow) => Promise<ModelRow | null>;

    private static readonly HOURLY_ALLOCATION_NONE = "N"; // mirror backend default
    private static readonly HOURLY_ALLOCATION_COUNT = "M"; // equal count
    private static readonly HOURLY_ALLOCATION_SUM = "D";   // distance sum ratio
    private static readonly PAY_METHOD_HOURLY = "H";
    private static readonly PAY_METHOD_SALARY = "S";

    static isDriverSettlementLayoutTab(layout: Layout): boolean {
        return "getLayoutBaseFilter" in layout && "tableDriverSettlement" in layout;
    }

    onLoad(): void | Promise<void> {
        this.splitterDriverSettlement.style.overflow = "auto";
        this.sourceVoidedPayablesDriverSettlement.search({ is_driver_settlement: true });
        this.sourceStateFederalTaxDriverSettlement.search();
        this.tabsetDriverSettlement.forEveryChildComponent((component) => {
            if (component instanceof Layout && DriverSettlement.isDriverSettlementLayoutTab(component)) {
                const table = (component as DriverSettlementTabLayout).tableDriverSettlement;
                table.addSelectionListener((event: TableSelectionEvent) => {
                    if (event.newRow?.data?.get("record_type") === "S") {
                        table.detailLayout = "lme/settlement/Settlement";
                    } else {
                        table.detailLayout = "lme/settlement/PendingPayAdjustment";
                    }
                });

                table.addRowDisplayListener((event: TableRowDisplayEvent) => {
                    const tableRow = event.getTableRow();
                    const data = tableRow.data as ModelRow;
                    if (component instanceof DriverSettlementProcess) {
                        // if allocation order pay was calculated, set as pay amount
                        data.set("settlement_amount", data.getCurrency("hourly_alloc_order_pay") || data.getCurrency("pay_amount"));
                        OrderPayablesUtil.setPayAdjustmentInfo(tableRow, this.layoutGroupBy, this.payablesLookupMap, "settlement_amount");
                    } else {
                        OrderPayablesUtil.setPayAdjustmentInfo(tableRow, this.layoutGroupBy, this.payablesLookupMap);
                    }
                });

                (component as DriverSettlementTabLayout).getProfileRow = () => this.profileRow;
                if (component instanceof DriverSettlementProcess) {
                    component.getPayablesLookupMap = () => this.payablesLookupMap;
                    component.getPayeeLookupMap = () => this.payeeDataLookup;
                    component.allGroupByRows = () => this.layoutGroupBy.tableGroupBy.rows;
                }
            }
        });
        this.layoutheaderDriverSettlement.addRightTools(OrderPayablesUtil.generateAddButton());
        this.setupPayeeTotalsLayout();
    }

    async loadProfile(profile?: string | ModelRow) {
        if (profile instanceof ModelRow) {
            await this.setProfileRow(profile);
            return;
        }
        const profileId = profile ?? LMEUserSettings.get()?.driver_settlement_profile;
        if (profileId == null)
            Snackbar.showSnackbar("A default Driver Settlement profile is not set. One can be set via User Settings.", { persist: true });
        else {
            const profileRow = await new ModelDriverSettlementProfile().searchSingle({ id: profileId }, "lme/settlement/driver-settlement/DriverSettlementProfileFilter");
            if (profileRow == null) {
                Snackbar.showSnackbar("The profile [" + profileId + "] was not found.", { persist: true });
            } else {
                await this.setProfileRow(profileRow);
            }
        }
    }

    public get profileRow(): RowDriverSettlementProfile {
        return this._profileRow;
    }

    public async setProfileRow(value: RowDriverSettlementProfile) {
        if (value != this._profileRow) {
            this._profileRow = value;
            await this.setPayAdjustmentCodes();
            if (!await this.checkBonusTaxRatesSetup()) {
                this.layoutGroupBy.tableGroupBy.selectedRow = null;
                this.layoutGroupBy.tableGroupBy.clearRows();
                return;
            }
            this.profileFilter = new DriverSettlementFilter(value);
            if (this._profileRow != null) {
                this.buttonTimecardProcessing.visible = this.profileRow.get("payee_type") !== "O" && this.profileRow.isNull("bonus_code");
                //check control dates
                const { companyCutoffDate, ownerCutoffDate, profileCutoffDate } = this.setCutoffDates();

                if (!this.cutoffDatesSet(companyCutoffDate, ownerCutoffDate)) {
                    return;
                }

                this.setUpdateCutoffDateTooltip(this.profileRow);
                this.sourceOrderPayablesCache.search({ profile_row: this.profileRow, is_driver_settlement: true });
                this.sourcePayeeCache.search();
            }
        }
    }

    public setCutoffDates(): {
        companyCutoffDate: Date | null;
        ownerCutoffDate: Date | null;
        profileCutoffDate: Date | null;
    } {
        this.snackbars.clear();
        const companyCutoffDate = this.getPayrollDateFromUrl("comp_cutoff_date");
        const ownerCutoffDate = this.getPayrollDateFromUrl("ownop_cutoff_date");
        this.payrollCutoffDate = this.getPayrollCutoffDate(companyCutoffDate, ownerCutoffDate);
        const profileCutoffDate = DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date"));
        this.recurringDate = this.getEarlierCutoffDate(this.payrollCutoffDate, profileCutoffDate);
        this.setProcessPayrollDates();
        this.textboxPayrollCutoffDate.text = DateUtil.formatDate(DateUtil.justDate(this.payrollCutoffDate));
        this.textboxOverrideCutoffDate.text = DateUtil.formatDate(profileCutoffDate);
        this.setOverrideCutoffDateTooltip(profileCutoffDate, this.payrollCutoffDate);
        return { companyCutoffDate, ownerCutoffDate, profileCutoffDate };
    }

    private getPayrollDateFromUrl(field: keyof RowPayrollControlFields): Date | null {
        if (this.payrollControl) {
            return !this.payrollControl.isNull(field) ? new Date(this.payrollControl.get(field)) : null;
        } else {
            return getPayrollControlValue(field) ? new Date(getPayrollControlValue(field)) : null;
        }
    }

    private setProcessPayrollDates() {
        if (this.payrollCutoffDate) {
            this.layoutProcess.payrollCutoffDate = this.payrollCutoffDate;
        }
        this.layoutProcess.recurringDate = this.recurringDate;
    }

    private setUpdateCutoffDateTooltip(profile: ModelRow) {
        const updateButton = new Button({
            caption: "Next Cutoff Date",
            color: "primary",
            onClick: async () => {
                const response = await this.updateCutoffDate(profile);
                if (response) {
                    this.setCutoffDates();
                    this.profileFilter = new DriverSettlementFilter(this.profileRow, this.payrollCutoffDate);
                    this.sourceOrderPayablesCache.search({ profile_row: this.profileRow, is_driver_settlement: true });
                    this.sourcePayeeCache.search({ is_settlement: true });
                }
            }
        });
        this.textboxPayrollCutoffDate.tooltipDynamicContent = updateButton;
    }

    private setOverrideCutoffDateTooltip(profileCutoffDate: Date, payrollCutoffDate: Date) {
        if (!profileCutoffDate) {
            this.textboxOverrideCutoffDate.tooltip = "Using the Payroll Cutoff Date to select " +
                "all records: settlements, pending pay adjustments, and recurring pay " +
                "adjustments.";
            return;
        }

        if (profileCutoffDate > payrollCutoffDate) {
            this.textboxOverrideCutoffDate.color = "error";
            this.textboxOverrideCutoffDate.tooltip = "Using the Override Cutoff Date to select " +
                "settlement and pending pay adjustment records. Using the Payroll Cutoff " +
                "Date (earlier date) to select recurring pay adjustments.";
        } else {
            this.textboxOverrideCutoffDate.color = "default";
            this.textboxOverrideCutoffDate.tooltip = "Using the Override Cutoff Date (earlier " +
                "date) to select all records: settlements, pending pay adjustments, and " +
                "recurring pay adjustments.";
        }

    }

    private getPayrollCutoffDate(companyCutoffDate: Date, ownerCutoffDate: Date): Date {
        const payeeType = this._profileRow.get("payee_type");

        if (this._profileRow.isNull("payee_type")) {
            // Show warning if dates don't match
            if (companyCutoffDate && ownerCutoffDate && companyCutoffDate.getTime() !== ownerCutoffDate.getTime()) {
                const panel = this.createWarningSnackbar("Payment cycles for payee types do not match. The earliest Payroll Cutoff date is applied.", null, false);
                this.snackbars.add(panel);
            }
            // Return the earlier date, or the matching date if they're equal
            return this.getEarlierCutoffDate(companyCutoffDate, ownerCutoffDate);
        }

        return payeeType === "O" ? ownerCutoffDate : companyCutoffDate;
    }

    private getEarlierCutoffDate(date1: Date, date2: Date): Date {
        return date1 && date2
            ? (date1 <= date2 ? date1 : date2)
            : date1 || date2;
    }

    private cutoffDatesSet(companyCutoffDate: Date, ownerCutoffDate: Date): boolean {
        const requiredDates = [];
        if (this._profileRow.isNull("payee_type")) {
            if (!companyCutoffDate) requiredDates.push("Company");
            if (!ownerCutoffDate) requiredDates.push("Owner Operator");
        } else if (this._profileRow.get("payee_type") === "C" && !companyCutoffDate) {
            requiredDates.push("Company");
        } else if (this._profileRow.get("payee_type") === "O" && !ownerCutoffDate) {
            requiredDates.push("Owner Operator");
        }

        if (requiredDates.length > 0) {
            const panel = this.createWarningSnackbar(`${requiredDates.join(" and ")} Cutoff Date${requiredDates.length > 1 ? "s" : ""} must be entered on Settlement Control`, null, false);
            this.snackbars.add(panel);
            Snackbar.showSnackbar(panel, { persist: true });
            return false;
        }
        return true;
    }

    async setPayAdjustmentCodes() {
        if (!this.profileRow.isNull("bonus_code")) {
            const payAdjustmentCodes = [];
            const result: ModelSearchResult = await Model.search("lme/settlement/bonus-code-dtl", { bonus_code_hdr_id: this.profileRow.get("bonus_code") });
            result.modelRows?.forEach((row: ModelRow) => {
                if (row.isNull("deduct_code_id")) return;
                payAdjustmentCodes.push(row.get("deduct_code_id"));
            });
            (this.profileRow as ModelRow).set("pay_adjustment_codes", payAdjustmentCodes);
        }
    }

    async checkBonusTaxRatesSetup(): Promise<boolean> {
        if (this.checkingBonusTaxRates) return false;
        this.checkingBonusTaxRates = true;
        const bonusTaxMethod = this.profileRow.get("bonus_tax_method");
        this.snackbars.clear();
        let isValid = true;
        if (bonusTaxMethod === "B") {
            const fedTaxTable = await Model.searchSingleRecord("lme/settlement/fedtaxtable");
            if (fedTaxTable == null || fedTaxTable.isNull("bonus_tax_rate")) {
                const panel = this.createWarningSnackbar("The Bonus Tax Rate is not set in the Federal Tax Table. Please set it before processing driver settlements.", () => {
                    return Navigation.navigateTo("lme/settlement/FederalTaxTable", { newTab: true });
                }, true);
                this.snackbars.add(panel);
                Snackbar.showSnackbar(panel, { persist: true });
                isValid = false;
            }
            const stateLocalTaxTables = await Model.search("lme/ar/statetax", { type_of: "S" });
            if (stateLocalTaxTables.modelRows.length === 0) {
                const panel = this.createWarningSnackbar("No State Tax Tables are set up. Please set them up before processing driver settlements.", () => {
                    return Navigation.navigateTo("lme/settlement/StateTaxTableList", { newTab: true });
                }, true);
                this.snackbars.add(panel);
                Snackbar.showSnackbar(panel, { persist: true });
                isValid = false;
            } else {
                const stateLocalTaxTableIds = [];
                for (const taxTable of stateLocalTaxTables.modelRows) {
                    if (taxTable.isNull("bonus_tax_rate")) {
                        stateLocalTaxTableIds.push(taxTable.get("id"));
                    }
                }
                if (stateLocalTaxTableIds.length > 0) {
                    const panel = this.createWarningSnackbar(`The Bonus Tax Rate is not set for all State Tax Tables. Please set it before processing driver settlements.`, () => {
                        return Navigation.navigateTo("lme/settlement/StateTaxTableList?ids=" + encodeURIComponent(stateLocalTaxTableIds.join(",")), { newTab: true });
                    }, true);
                    this.snackbars.add(panel);
                    Snackbar.showSnackbar(panel, { persist: true });
                    isValid = false;
                }
            }
        }
        this.checkingBonusTaxRates = false;
        return isValid;
    }

    private createWarningSnackbar(message: string, onClick: () => {}, includeButton: boolean): Panel {
        const panel = new Panel();
        const label = new Label({ text: message });
        const button = new Button({
            id: "buttonOpenRecord",
            caption: "Open Record",
            onClick: onClick
        });
        panel.add(label);
        if (includeButton) {
            panel.add(button);
        }
        return panel;
    }

    sourceOrderPayablesCacheAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.payablesLookupMap = new PayablesLookup();
            this.payeeDataLookup = new PayeeDataLookup<SettlementPayeeData>(() => new SettlementPayeeData());
            this.profileFilter.payablesLookupMap = this.payablesLookupMap;
            this.layoutGroupBy.initialize(this.tabsetDriverSettlement, this.profileFilter, this.payeeDataLookup, this.profileRow, this.sourcePayeeCache, this.updateTimecardProcessingButton.bind(this));
            this.setupMinuteTimer();
        }
    }

    sourceOrderPayablesCacheOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let changeType = event.change.type as ModelDataChangeType;
        const changeData = event.change.data as ModelRow;
        if (!this.profileFilter.dataPasses(changeData)) {
            changeType = ModelDataChangeType.DELETE;
        }
        const payeeData = this.payeeDataLookup.getPayeeData(changeData.get("payee_id"));
        let oldData: ModelRow;
        if (payeeData) {
            oldData = payeeData.findPayableById(changeData.get("record_id"));
        } else {
            oldData = this.sourceOrderPayablesCache.data.find(row => row.get("record_id") == changeData.get("record_id"));
        }
        if (this.sourceOrderPayablesCache.isSearching || (oldData != null && ObjectUtil.deepEqual(oldData.data, changeData.data))) {
            return;
        }
        this.sourceOrderPayablesCache.handleDataChange({
            type: changeType,
            data: changeData,
            lastUpdateNanos: event.change.lastUpdateNanos
        });
        this.layoutGroupBy.updateGroupByTableByPayable(oldData, changeData, changeType).then(() => {
            this.rollUpPayAdjustments(changeData);
            this.calculateHourlyAllocation(changeData.get("payee_id"));
        });
        event.preventDefault();
    }

    sourceVoidedPayablesDriverSettlementOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        const changeData = event.change.data;
        if (changeData.isNull("table_name")) return;
        const row = this.sourceVoidedPayablesDriverSettlement.data.find(row => row.get("id") == changeData.get("id"));
        if (changeData.get("ready_to_pay_flag") == "V" && row == null) {
            this.sourceVoidedPayablesDriverSettlement.data.push(changeData);
        } else if (changeData.get("ready_to_pay_flag") != "V" && row != null) {
            this.sourceVoidedPayablesDriverSettlement.data = this.sourceVoidedPayablesDriverSettlement.data.filter(row => row.get("id") != changeData.get("id"));
        }
        this.updateArchiveVoidsButton();
        event.preventDefault();
    }

    sourceVoidedPayablesDriverSettlementAfterExecution(event: DataSourceExecutionEvent) {
        if (event.getAction() == DataSourceAction.SEARCH) {
            this.updateArchiveVoidsButton();
        }
    }

    sourcePayeeCacheOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let changeType = event.change.type as ModelDataChangeType;
        const changeData = event.change.data as ModelRow;
        if (changeData.isNull("payee_id")) return;
        if (!this.profileFilter.dataPassesPayee(changeData) && !this.profileFilter.dataPassesNewPayee(changeData)) {
            changeType = ModelDataChangeType.DELETE;
        }
        const oldData = this.sourcePayeeCache.data.find(row => row.get("payee_id") == changeData.get("payee_id"));
        if (oldData) {
            this.payeeDataLookup.getPayeeData(oldData.get("payee_id")).hasTaxUpdates = true;
        }
        if (oldData != null && ObjectUtil.deepEqual(oldData.data, changeData.data)) {
            this.updatePayeeTotalsRefreshLabel();
            return;
        }
        this.sourcePayeeCache.handleDataChange({
            type: changeType as ModelDataChangeType,
            data: changeData,
            lastUpdateNanos: event.change.lastUpdateNanos
        });
        this.layoutGroupBy.updateGroupByTableByPayee(oldData, changeData, changeType as ModelDataChangeType).then(() => {
            this.updateTimecardProcessingButton();
            this.calculateHourlyAllocation(changeData.get("payee_id"));
        });
        event.preventDefault();
    }

    private updateArchiveVoidsButton() {
        this.buttonArchiveVoids.caption = `Voids Ready To Archive (${this.sourceVoidedPayablesDriverSettlement.data.length})`;
        this.buttonArchiveVoids.enabled = this.sourceVoidedPayablesDriverSettlement.data.length > 0;
    }

    private updateTimecardProcessingButton() {
        this.populateSourceDrsTimecard();
        const totalTimecards = this.sourceDrsTimecard.data.length;
        const updatedTimecards = this.sourceDrsTimecard.data.filter(row => !row.getCurrency("total_pay", new Currency(0)).isZero());
        this.buttonTimecardProcessing.caption = `Timecard Processing (${updatedTimecards.length}/${totalTimecards})`;
    }

    private populateSourceDrsTimecard() {
        const timecardData: ModelRow[] = [];
        for (const row of this.layoutGroupBy.tableGroupBy.rows) {
            const payeeData = this.payeeDataLookup.getPayeeData(row.data.get("payee_id"));
            const timecardRow = payeeData.getTimecard();
            if (timecardRow && this.shouldTimecardBeIncluded(payeeData.getPayeeRow())) {
                timecardData.push(timecardRow);
            }
        }
        this.sourceDrsTimecard.data = timecardData;
    }

    shouldTimecardBeIncluded(payeeRow: ModelRow): boolean {
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            const payeeCurrency = payeeRow.get("payee_currency");
            const currencyMatchesProfile = payeeCurrency === this.profileRow.get("currency_type");
            const currencyMatchesFunctional = payeeCurrency == null && this.profileRow.get("currency_type") === CurrencySettings.getSingleton().getFunctionalCurrency();
            return currencyMatchesProfile || currencyMatchesFunctional;
        } else {
            return true;
        }
    }

    private async createTimecardsAndUpdateRates() {
        for (const groupByRow of this.layoutGroupBy.tableGroupBy.rows) {
            const payeeId = groupByRow.data.get("payee_id");
            const payeeData = this.payeeDataLookup.getPayeeData(payeeId);
            const existingTimecard = this.sourceDrsTimecard.data.find(row => row.get("id") === payeeId);
            if (existingTimecard) {
                // update rates if needed
                const payeeRow = payeeData.getPayeeRow();
                const payMethod = payeeRow.get("pay_method");
                const regRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "regular_rate", payMethod);
                const otRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "overtime_rate", payMethod);
                const vacRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "vacation_rate", payMethod);
                const changed = regRate !== existingTimecard.get("regular_rate", 0) || otRate !== existingTimecard.get("overtime_rate", 0) || vacRate !== existingTimecard.get("vacation_rate", 0);
                if (changed) {
                    existingTimecard.set("regular_rate", regRate);
                    existingTimecard.set("overtime_rate", otRate);
                    existingTimecard.set("vacation_rate", vacRate);
                    TimecardProcessing.recalculateHoursAndTotals(existingTimecard);
                    await existingTimecard.post();
                    // also update the cached timecard rates to avoid the delay from live updates
                    payeeRow.set("tc_regular_rate", regRate);
                    payeeRow.set("tc_overtime_rate", otRate);
                    payeeRow.set("tc_vacation_rate", vacRate);
                }
            } else if (this.shouldTimecardBeIncluded(payeeData.getPayeeRow())) {
                const timecardRow = payeeData.addTimecard();
                if (timecardRow) {
                    timecardRow.set("payperiod_end", this.profileRow.getDate("cutoff_date", new Date()));
                    const payeeRow = payeeData.getPayeeRow();
                    const payMethod = payeeRow.get("pay_method");
                    const regRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "regular_rate", payMethod);
                    const otRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "overtime_rate", payMethod);
                    const vacRate = TimecardProcessing.calculateHourlyRateFromPayee(payeeRow, "vacation_rate", payMethod);
                    timecardRow.set("regular_rate", regRate);
                    timecardRow.set("overtime_rate", otRate);
                    timecardRow.set("vacation_rate", vacRate);
                    TimecardProcessing.recalculateHoursAndTotals(timecardRow);
                    await timecardRow.post();
                    // cache for quick access
                    payeeRow.set("tc_regular_rate", regRate);
                    payeeRow.set("tc_overtime_rate", otRate);
                    payeeRow.set("tc_vacation_rate", vacRate);
                    this.sourceDrsTimecard.data.push(timecardRow);
                }
            }
        }
        this.sourceDrsTimecard.data.forEach((row) => row._appending = false);
    }

    public rollUpPayAdjustments(newData: ModelRow) {
        if (newData.get("record_type") == "S") {
            const key = PayablesLookup.key(newData);
            const adjustmentsData = this.payablesLookupMap.getAdjustmentsWithKey(key);
            for (const [, modelRow] of adjustmentsData) {
                if (OrderPayablesUtil.matchingPayable(modelRow, newData) && modelRow.get("record_type") == "P") {
                    const index = this.sourceOrderPayablesCache.findRowInData(modelRow);
                    this.sourceOrderPayablesCache.redisplayRow(index, modelRow, ModelDataChangeType.UPDATE);
                }
            }
        } else if (newData.get("record_type") == "P") {
            const key = PayablesLookup.key(newData);
            const modelRow = this.payablesLookupMap.getSettlementWithKey(key);
            if (OrderPayablesUtil.matchingPayable(modelRow, newData) && modelRow.get("record_type") == "S") {
                const index = this.sourceOrderPayablesCache.findRowInData(modelRow);
                this.sourceOrderPayablesCache.redisplayRow(index, modelRow, ModelDataChangeType.UPDATE);
            }
        }
    }

    buttonArchiveVoidsOnClick(event: ClickEvent) {
        const cutoffDate = DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date"));
        ArchiveVoidedPayables.showSlideout({ cutoff_date: cutoffDate, is_driver_settlement: true });
    }

    override get windowTitle(): string {
        const title = "Driver Settlement";
        const profileId = this.profileRow?.getString("id");
        return profileId ? `${title} - ${profileId}` : title;
    }

    public async updateSelectedPayee() {
        if (this.payeeDataLookup) {
            // Check if the currently selected payee has changes and the panel is expanded
            if (this.currentSelectedPayee && this.layoutPayeeTotals.expanded) {
                const selectedPayeeData = this.payeeDataLookup.getPayeeData(this.currentSelectedPayee.payeeId);
                if (selectedPayeeData && await selectedPayeeData.hasChanges()) {
                    // Update the selected payee's totals by calling the search method
                    const profileCutoffDate = DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date"), true, false);
                    const cutoffDate = profileCutoffDate ?? this.payrollCutoffDate;
                    await this.layoutPayeeTotals.searchData(this.currentSelectedPayee, cutoffDate, this.recurringDate);

                    // Cache the API response
                    if (this.layoutPayeeTotals.activeRow) {
                        selectedPayeeData.setCachedApiResponse(this.layoutPayeeTotals.activeRow);
                        await selectedPayeeData.setTotals();
                        selectedPayeeData.hasTaxUpdates = false; // Reset tax updates after fetching
                    }
                }
            }
        }
    }

    private setTimer() {
        this.remainingSeconds = 30;
        this.timerId = window.setTimeout(() => this.executeTimerUpdates(), 30000); // 30 seconds
        this.startCountdown();
    }

    private setupMinuteTimer() {
        this.setTimer();
        this.addUnmountListener(() => this.clearTimer());
    }

    private executeTimerUpdates() {
        this.updateSelectedPayee().then(() => {
            this.setTimer();
        });
    }

    private clearTimer() {
        if (this.timerId !== null) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }
        if (this.countdownTimerId !== null) {
            clearInterval(this.countdownTimerId);
            this.countdownTimerId = null;
        }
    }

    private startCountdown() {
        if (this.countdownTimerId !== null) {
            clearInterval(this.countdownTimerId);
        }
        this.countdownTimerId = window.setInterval(() => {
            this.remainingSeconds--;
            this.updatePayeeTotalsRefreshLabel();
            if (this.remainingSeconds <= 0) {
                clearInterval(this.countdownTimerId);
                this.countdownTimerId = null;
            }
        }, 1000);
        this.updatePayeeTotalsRefreshLabel();
    }

    private async updatePayeeTotalsRefreshLabel() {
        if (this.currentSelectedPayee && this.layoutPayeeTotals.expanded) {
            const selectedPayeeData = this.payeeDataLookup.getPayeeData(this.currentSelectedPayee.payeeId);
            if (selectedPayeeData && selectedPayeeData.getTotalsLastChangedDateTime() != null) {
                if (await selectedPayeeData.hasChanges(false)) {
                    this.layoutPayeeTotals.labelRefresh.visible = true;
                    this.layoutPayeeTotals.labelRefresh.text = `Refresh (${this.remainingSeconds}s)`;
                } else {
                    this.layoutPayeeTotals.labelRefresh.visible = false;
                }
            } else {
                this.layoutPayeeTotals.labelRefresh.visible = false;
            }
        } else {
            this.layoutPayeeTotals.labelRefresh.visible = false;
        }
    }

    setupPayeeTotalsLayout() {
        // Set up the updateRefreshLabel callback
        this.layoutGroupBy.calculateAllocation = this.calculateHourlyAllocation.bind(this);
        this.layoutPayeeTotals.updateRefreshLabel = () => this.updatePayeeTotalsRefreshLabel();
        this.layoutGroupBy.onPayeeSelected = async (props: PayeeSelectedProps) => {
            this.currentSelectedPayee = props;
            this.layoutPayeeTotals.visible = true;
            this.layoutPayeeTotals.refresh = async (forceUpdate: boolean = false) => {
                this.layoutPayeeTotals.labelPayeeId.text = this.layoutPayeeTotals.labelPayeeId.tooltip = props.payeeName;
                if (this.currentSelectedPayee && this.layoutPayeeTotals.expanded) {
                    this.layoutPayeeTotals.labelPayeeId.busy = true;
                    const selectedPayeeData = this.payeeDataLookup.getPayeeData(this.currentSelectedPayee.payeeId);
                    this.layoutPayeeTotals.panelTimecard.visible = false;
                    this.layoutPayeeTotals.labelViewTimecard.visible = false;
                    if (selectedPayeeData) {
                        // Check if we have cached data and totals haven't changed
                        if (selectedPayeeData.hasCachedApiResponse() && await selectedPayeeData.hasChanges() === false && !forceUpdate) {
                            // Use cached data instead of making API call
                            if (this.layoutPayeeTotals.activeRow) {
                                this.layoutPayeeTotals.activeRow.clear();
                                this.layoutPayeeTotals.activeRow.setValues(selectedPayeeData.getCachedApiResponse().data);
                                this.layoutPayeeTotals.hidePanelsFieldsWithNoData();
                            }
                        } else {
                            // Make API call to get fresh data
                            const profileCutoffDate = DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date"), true, false);
                            const cutoffDate = profileCutoffDate ?? this.payrollCutoffDate;
                            await this.layoutPayeeTotals.searchData(this.currentSelectedPayee, cutoffDate, this.recurringDate);

                            // Cache the API response
                            if (this.layoutPayeeTotals.activeRow) {
                                selectedPayeeData.setCachedApiResponse(this.layoutPayeeTotals.activeRow.createBasicCopy());
                                await selectedPayeeData.setTotals();
                                selectedPayeeData.hasTaxUpdates = false; // Reset tax updates after fetching
                            }
                        }
                    }
                }
                this.layoutPayeeTotals.labelPayeeId.busy = false;
                // Update refresh label after refreshing data
                this.updatePayeeTotalsRefreshLabel();
            };
            this.layoutPayeeTotals.viewTimecardOnClick = async () => {
                await this.createTimecardsAndUpdateRates();
                TimecardProcessing.showCrud({
                    dataSource: this.sourceDrsTimecard,
                    payeeId: props.payeeName,
                    payeeDataLookup: this.payeeDataLookup
                });
            };
            this.layoutPayeeTotals.onRecurringsClick = () => {
                const cutoffDate = this.getEarlierCutoffDate(DateUtil.parseDateWithKeywords(this.profileRow.get("cutoff_date")), this.payrollCutoffDate);
                const currency = CurrencySettings.getSingleton().multiCurrencyEnabled() ? this.profileRow.get("currency_type") : null;
                this.navigateToRecurringPayAdjustment(props.payeeId, cutoffDate, currency);
            };
            if (this.layoutPayeeTotals.expanded) {
                this.layoutPayeeTotals.refresh();
            }
            // Update refresh label visibility and text
            this.updatePayeeTotalsRefreshLabel();

        };
        this.layoutGroupBy.onPayeeDeselected = () => {
            this.currentSelectedPayee = null;
            this.layoutPayeeTotals.visible = false;
            this.layoutPayeeTotals.expanded = false;
            this.layoutPayeeTotals.labelRefresh.visible = false;
        };
    }

    sourceStateFederalTaxDriverSettlementOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        const changeData = event.change.data;
        if (changeData.isNull("payee_id")) {
            for (const payeeData of this.payeeDataLookup.getAllPayeeData()) {
                payeeData.hasTaxUpdates = true;
            }
        } else {
            // if payee updated is currently selected payee and panel is expanded, mark for refresh
            if (this.currentSelectedPayee && this.layoutPayeeTotals.expanded && this.currentSelectedPayee.payeeId === changeData.get("payee_id")) {
                this.payeeDataLookup.getPayeeData(changeData.get("payee_id")).hasTaxUpdates = true;
            }
        }
        // Update refresh label to show pending updates
        this.updatePayeeTotalsRefreshLabel();
        event.preventDefault();
    }

    async buttonTimecardProcessingOnClick(event: ClickEvent) {
        this.buttonTimecardProcessing.busy = true;
        await this.createTimecardsAndUpdateRates();
        TimecardProcessing.showCrud({ dataSource: this.sourceDrsTimecard, payeeDataLookup: this.payeeDataLookup });
        this.buttonTimecardProcessing.busy = false;
    }

    private navigateToRecurringPayAdjustment(payeeId: string, cutoffDate: Date, currency?: string) {
        const urlParams: string[] = [];
        urlParams.push(`payeeId=${payeeId}`);
        urlParams.push(`cutoffDate=${DateUtil.formatDate(cutoffDate, "yyyy-MM-dd")}`);
        if (currency) {
            urlParams.push(`currency=${currency}`);
        }
        Navigation.navigateTo("lme/settlement/RecurringPayAdjustmentList" + UrlUtil.buildQueryString(urlParams), { newTab: true });
    }

    public async calculateHourlyAllocation(payeeId: string): Promise<void> {
        const payeeData = this.payeeDataLookup.getPayeeData(payeeId);
        if (!payeeData) {
            return;
        }
        const payeeRow = payeeData.getPayeeRow();
        if (!payeeRow) {
            return;
        }

        // Only company drivers (C) with hourly or salary settlements
        if (payeeRow.get("payee_type") !== "C") return;

        // Get allocation method from payee row or payroll control fallback
        const allocationMethod = (payeeRow.get("hourly_pay_allocation")
            || getPayrollControlValue("hourly_pay_allocation")
            || DriverSettlement.HOURLY_ALLOCATION_NONE) as string;

        // Collect ready-to-pay hourly settlements (exclude Hold 'N' and Void 'V')
        const readyHourlySettlements: ModelRow[] = payeeData.getReadyToPaySettlements().filter(row => {
            const pm = row.get("settlement_pay_method");
            const status = row.get("ready_to_pay_flag");
            return (pm === DriverSettlement.PAY_METHOD_HOURLY || pm === DriverSettlement.PAY_METHOD_SALARY)
                && status === "Y"; // Exclude Hold (N) & Void (V)
        });

        // Timecard total pay required
        const timecardRow = payeeData.getTimecard();
        if (!timecardRow) {
            return;
        }
        const totalPayCurrency = timecardRow.getCurrency("total_pay", new Currency(0));

        // Prepare allocation based on method
        const runningAllocated = new Currency(0);
        if (totalPayCurrency.getCurrencyCode()) {
            runningAllocated.setCurrencyCode(totalPayCurrency.getCurrencyCode());
        }

        if (allocationMethod === DriverSettlement.HOURLY_ALLOCATION_SUM) {
            // Sum of pay_distance ratios
            const totalDistance = readyHourlySettlements.reduce((acc, row) => acc + (row.get("pay_distance") || 0), 0);
            // If totalDistance == 0 then treat as NONE (all zero)
            if (totalDistance <= 0) {
                for (const row of readyHourlySettlements) {
                    row.set("hourly_alloc_order_pay", new Currency(0));
                    this.displayAllocation(row);
                }
                return;
            }
            for (const row of readyHourlySettlements) {
                const payDistance = row.get("pay_distance") || 0;
                const ratio = payDistance / totalDistance;
                const allocCurrency = new Currency(totalPayCurrency);
                if (row === readyHourlySettlements[readyHourlySettlements.length - 1]) {
                    // Residual for last row to avoid rounding drift
                    await allocCurrency.subtract(runningAllocated);

                    if (allocCurrency.isNegative()) {
                        allocCurrency.amount = 0;
                    }
                } else {
                    allocCurrency.multiply(ratio);
                    runningAllocated.add(allocCurrency);
                }
                row.set("hourly_alloc_order_pay", allocCurrency);
                this.displayAllocation(row);
            }
        } else if (allocationMethod === DriverSettlement.HOURLY_ALLOCATION_COUNT) {
            const count = readyHourlySettlements.length;
            const equalRatio = count === 0 ? 0 : 1 / count;
            for (const row of readyHourlySettlements) {
                const allocCurrency = new Currency(totalPayCurrency);
                if (row === readyHourlySettlements[readyHourlySettlements.length - 1]) {
                    allocCurrency.subtract(runningAllocated);
                    if (allocCurrency.isNegative()) {
                        allocCurrency.amount = 0;
                    }
                } else {
                    allocCurrency.multiply(equalRatio);
                    runningAllocated.add(allocCurrency);
                }
                row.set("hourly_alloc_order_pay", allocCurrency);
                this.displayAllocation(row);
            }
        } else { // NONE or unsupported method: zero allocations
            for (const row of readyHourlySettlements) {
                row.set("hourly_alloc_order_pay", new Currency(0));
                this.displayAllocation(row);
            }
        }
    }

    private displayAllocation(row: ModelRow) {
        if (row.getCurrency("hourly_alloc_order_pay")) {
            const index = this.sourceOrderPayablesCache.findRowInData(row);
            this.sourceOrderPayablesCache.redisplayRow(index, row, ModelDataChangeType.UPDATE);
        }
    }
}
